import { z } from 'zod';
/** A Mongo ObjectId as it appears on the wire. */
export const objectId = z.string().regex(/^[a-f\d]{24}$/i, 'Choose an option from the list');
export const isoDate = z
    .union([z.string(), z.date()])
    .transform((value) => (value instanceof Date ? value.toISOString() : value))
    .refine((value) => !Number.isNaN(Date.parse(value)), 'Invalid date');
/** Amounts cross the wire as integer minor units. */
export const minorAmount = z
    .number()
    .int('Amount must be an integer number of paise')
    .nonnegative('Amount cannot be negative');
export const signedMinorAmount = z.number().int();
export const ifsc = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Enter an 11-character IFSC, e.g. HDFC0001234');
export const gstin = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z]\dZ[A-Z\d]$/, 'Enter a 15-character GSTIN, e.g. 29ABCDE1234F1Z5');
export const pan = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{5}\d{4}[A-Z]$/, 'Enter a 10-character PAN, e.g. ABCDE1234F');
/** A bank's SWIFT/BIC: 8 or 11 characters. */
export const swiftBic = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/, 'Enter an 8- or 11-character SWIFT/BIC, e.g. HDFCINBB');
/** An IBAN, spaces allowed on input and removed. */
export const iban = z
    .string()
    .trim()
    .toUpperCase()
    .transform((value) => value.replace(/\s/g, ''))
    .refine((value) => /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(value), 'Enter a valid IBAN, e.g. AE070331234567890123456');
/**
 * A local clearing code outside India: an ABA routing number, a UK sort code,
 * a BSB. Formats differ by country, so only the shape is checked.
 */
export const routingCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9-]{3,20}$/, 'Use 3–20 letters, digits or dashes');
/**
 * A TDS section code such as 194C or 194IA. Only its shape is checked here;
 * whether the tenant's TDS Sections master holds it is checked on the server.
 */
export const tdsSectionCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{3}[A-Z]{0,3}$/, 'Enter a section such as 194C or 194IA');
/** A rate in basis points: 1000 is 10.00%, 75 is 0.75%. Integers only. */
export const basisPoints = z
    .number()
    .int('Rate must be an integer number of basis points')
    .min(0)
    .max(10_000, 'Rate cannot exceed 100%');
export const bankAccountNumber = z
    .string()
    .trim()
    .min(5, 'Account number is too short')
    .max(34, 'Account number is too long')
    .regex(/^[A-Za-z0-9]+$/, 'Account number must be alphanumeric');
export const paginationQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Filter axes available on every operational screen (PRD §33).
 *
 * These double as the authorisation axes: `applyOrgScope` on the server
 * refuses a value the caller is not scoped to, rather than silently returning
 * nothing, so this list and the user's org scope stay the same vocabulary.
 */
export const scopeQuery = z.object({
    companyId: objectId.optional(),
    groupId: objectId.optional(),
    regionId: objectId.optional(),
    verticalId: objectId.optional(),
    businessUnitId: objectId.optional(),
    locationId: objectId.optional(),
    departmentId: objectId.optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    minAmount: z.coerce.number().int().optional(),
    maxAmount: z.coerce.number().int().optional(),
    q: z.string().trim().max(200).optional(),
});
//# sourceMappingURL=common.js.map